import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KAT = { BG: "Bildungsgutschein", AL: "Anwesenheitsliste", LZ: "Laufzettel", PV: "Praktikumsvertrag", PB: "Praktikumsbewertung", PR: "Prüfung", ZE: "Zertifikat", MV: "Maßnahmevertrag", DS: "Datenschutz", EG: "Erstgespräch", SO: "Sonstige" };

const ansehen = async (id) => {
  try {
    const r = await axios.get(`${API}/admin/mail-suche/treffer/${id}/datei`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  } catch (e) { toast.error("Datei nicht verfügbar"); }
};

const TnWahl = ({ id, tn, setTn }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (q.length < 2) { setItems([]); return undefined; }
    const h = setTimeout(() => axios.get(`${API}/admin/tn-search`, { params: { q, limit: 8 } }).then((r) => setItems(r.data.items || [])).catch(() => {}), 300);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <span className="relative">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tn ? "anderer TN …" : "Teilnehmer suchen …"} className="h-7 w-36 border rounded px-1" data-testid={`mail-treffer-tn-suche-${id}`} />
      {items.length > 0 && (
        <ul className="absolute z-20 mt-1 w-64 bg-background border rounded shadow text-xs max-h-48 overflow-y-auto">
          {items.map((x) => <li key={x.id}><button className="w-full text-left px-2 py-1 hover:bg-muted" onClick={() => { setTn(x); setQ(""); setItems([]); }} data-testid={`mail-treffer-tn-option-${x.id}`}>{x.nachname}, {x.vorname} · {x.kurs || "–"}</button></li>)}
        </ul>
      )}
    </span>
  );
};

const Treffer = ({ t, onDone }) => {
  const [kat, setKat] = useState(t.kategorie || "");
  const [tn, setTn] = useState(t.teilnehmer_id ? { id: t.teilnehmer_id, label: t.name } : null);
  const entscheiden = async (aktion) => {
    try { await axios.post(`${API}/admin/mail-suche/treffer/${t.id}`, { aktion, kategorie: kat, teilnehmer_id: tn?.id }); toast.success(aktion === "verwerfen" ? "Verworfen" : "In Akte abgelegt"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };
  const tnName = tn ? (tn.label || `${tn.nachname}, ${tn.vorname}`) : "Teilnehmer fehlt";
  return (
    <li className="py-1.5 flex flex-wrap items-center justify-between gap-2" data-testid={`mail-treffer-${t.id}`}>
      <span className="min-w-0"><b className={tn ? "" : "text-red-600"} data-testid={`mail-treffer-tn-${t.id}`}>{tnName}</b> · {t.dateiname} <span className="text-muted-foreground">· {t.postfach} · {(t.datum || "").slice(0, 10)} · {t.grund}</span></span>
      <span className="flex items-center gap-2 text-xs">
        <button className="underline" onClick={() => ansehen(t.id)} data-testid={`mail-treffer-ansehen-${t.id}`}>Ansehen</button>
        <TnWahl id={t.id} tn={tn} setTn={setTn} />
        <select value={kat} onChange={(e) => setKat(e.target.value)} className="h-7 border rounded px-1" data-testid={`mail-treffer-kat-${t.id}`}>
          <option value="">Kategorie …</option>{Object.entries(KAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button className="underline font-medium" disabled={!kat || !tn} onClick={() => entscheiden("uebernehmen")} data-testid={`mail-treffer-uebernehmen-${t.id}`}>Ablegen</button>
        <button className="underline text-muted-foreground" onClick={() => entscheiden("verwerfen")} data-testid={`mail-treffer-verwerfen-${t.id}`}>Verwerfen</button>
      </span>
    </li>
  );
};

export const MailSucheBlock = () => {
  const [st, setSt] = useState(null);
  const [offen, setOffen] = useState([]);
  const [bg, setBg] = useState(null);
  const [sum, setSum] = useState(null);
  const laden = () => {
    axios.get(`${API}/admin/mail-suche/zusammenfassung`).then((r) => setSum(r.data)).catch(() => {});
    axios.get(`${API}/admin/mail-suche/status`).then((r) => setSt(r.data)).catch(() => {});
    axios.get(`${API}/admin/mail-suche/treffer`).then((r) => setOffen(r.data)).catch(() => {});
  };
  useEffect(() => { laden(); }, []);
  useEffect(() => { if (!st?.laeuft) return undefined; const i = setInterval(laden, 5000); return () => clearInterval(i); }, [st?.laeuft]);
  const start = async () => {
    try { await axios.post(`${API}/admin/mail-suche/start`, {}); toast.success("Suche gestartet – läuft im Hintergrund"); laden(); }
    catch (e) { toast.error(e.response?.data?.detail || "Start fehlgeschlagen"); }
  };
  return (
    <div className="border rounded-lg p-4 mb-3" data-testid="mail-suche-block">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium">Postfach-Suche ab 01.07.2024 (jean, angelina, annika – Eingang, Gesendet, Archiv)</p>
          <p className="text-sm text-muted-foreground" data-testid="mail-suche-status">
            {st?.laeuft ? `Läuft: ${st.aktuell || "…"} · ${st.mails_geprueft || 0} Mails geprüft` : st?.fertig_am ? `Zuletzt fertig ${new Date(st.fertig_am).toLocaleString("de-DE")} · ${st.mails_geprueft} Mails geprüft` : "Noch nicht gelaufen"}
            {st ? ` · ${st.importiert} automatisch abgelegt · ${st.offen} zur Prüfung` : ""}{st?.fehler ? ` · Fehler: ${st.fehler}` : ""}
          </p>
          {st?.postfaecher && <p className="text-xs text-muted-foreground" data-testid="mail-suche-postfaecher">Postfächer: {st.postfaecher.join(", ")}</p>}
          {(st?.ordner_fehler || []).filter((f) => f.includes("Anmeldung")).map((f) => <p key={f} className="text-xs text-red-600">{f}</p>)}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => axios.get(`${API}/admin/mail-suche/bg-fehlt`).then((r) => setBg(r.data))} data-testid="mail-suche-bg-fehlt">BG fehlt</Button>
          <Button size="sm" disabled={st?.laeuft} onClick={start} data-testid="mail-suche-start">{st?.laeuft ? "Sucht …" : "Suche starten"}</Button>
        </div>
      </div>
      {sum && (sum.importiert || sum.offen) && (
        <div className="mt-3 text-sm grid sm:grid-cols-2 gap-2" data-testid="mail-suche-zusammenfassung">
          {[["importiert", "Automatisch abgelegt"], ["offen", "Zur Prüfung"]].map(([k, l]) => (
            <div key={k} className="border rounded p-2" data-testid={`mail-suche-sum-${k}`}>
              <p className="font-medium">{l}</p>
              <p className="text-xs text-muted-foreground">{Object.entries(sum[k] || {}).map(([kat, n]) => `${kat}: ${n}`).join(" · ") || "–"}</p>
              {k === "offen" && sum.ohne_tn > 0 && <p className="text-xs text-red-600">davon {sum.ohne_tn} ohne erkannten Teilnehmer</p>}
            </div>
          ))}
        </div>
      )}
      {bg && (
        <div className="mt-3 text-sm" data-testid="mail-suche-bg-liste">
          <p className="font-medium">{bg.length} Bildungsgutschein-Fälle ohne Bildungsgutschein (auch kein Verzicht-Formular) in der Akte</p>
          <ul className="max-h-48 overflow-y-auto text-xs text-muted-foreground">{bg.map((x) => <li key={x.teilnehmer_id}>{x.name} · {x.kurs || "–"} · Start {x.start || "–"}</li>)}</ul>
        </div>
      )}
      {offen.length > 0 && (
        <ul className="mt-3 text-sm divide-y max-h-96 overflow-y-auto" data-testid="mail-suche-offen">{offen.map((t) => <Treffer key={t.id} t={t} onDone={laden} />)}</ul>
      )}
    </div>
  );
};
